import { useState, useEffect, useRef } from 'react';

/**
 * This hook detects changes of screen resolution and evaluates query passed as an argument
 * @param query - media query that is going to be matched and evaluated
 * @return {boolean} - returns true if resolution matches passed query
 *
 * @example
 * ```
 * const match = useMediaQuery('(min-width: 768px)')
 * ```
 */
export const useMediaQuery = (query: string) => {
  const initialized = useRef<boolean>(false);
  const [matches, setMatches] = useState(false);

  if (query && !initialized.current) {
    setMatches(isMediaMatch(query));
    initialized.current = true;
  }

  useEffect(() => {
    window.addEventListener('resize', () => setMatches(isMediaMatch(query)));
    return () => {
      window.removeEventListener('resize', () => setMatches(isMediaMatch(query)));
    };
  }, [matches, query]);

  return matches;
};

const isMediaMatch = (query: string): boolean => window.matchMedia(query).matches;
